/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: WebSocket Implementation
 * PURPOSE: Message routing with handler registry pattern (35+ handlers) and business logic orchestration
 *
 * NEW PATTERNS IMPLEMENTED:
 * - Handler Registry: Map-based message router with O(1) lookup (vs O(n) switch)
 * - Middleware Pattern: Logging and performance monitoring via middleware chain
 * - Atomic Subscription: Race condition fix for table subscriptions
 *
 * KEY FEATURES:
 * - 35+ message handlers organized by domain (alerts, work orders, presence, state)
 * - Middleware support for cross-cutting concerns (logging, metrics)
 * - Integration with UnifiedDataUpdateOrchestrator for state mutations
 * - EventBus integration for domain service communication
 *
 * RELATED FILES:
 * - src/lib/services/websocket/websocket.state.svelte.ts
 * - src/lib/services/websocket/WebSocketTransport.ts
 * - src/lib/services/orchestration/UnifiedDataUpdateOrchestrator.svelte.ts
 */

/**
 * websocket.actions.svelte.ts - WebSocket Actions and Message Routing
 *
 * Stage F+1: Complete Migration (2025-10-01)
 *
 * Phase 3: Complete rewrite to use WebSocketTransport
 *
 * Architecture (New):
 * - WebSocketTransport.ts = Pure transport layer (connection only)
 * - websocket.state.svelte.ts = Reactive state + transport management
 * - websocket.actions.svelte.ts = Business logic and message routing (this file)
 *
 * Responsibilities:
 * - Message routing by type
 * - Table subscription management
 * - Business logic for alerts, work orders, presence
 * - Event emission for domain services
 */

import { browser } from '$app/environment';
import { createLogger } from '$lib/utils/logger';
import { eventBus } from '$lib/core/services/EventBusService';
import { MessageType } from '$lib/types/websocket/messages.types';
import { SvelteSet } from 'svelte/reactivity';
import type {
	DataDeltaMessage,
	DataSnapshotMessage,
	TableItem,
	TaskProgressMessage
} from '$lib/types/websocket/messages.types';
import {
	websocketState,
	viewerHelpers,
	registerMessageHandler,
	connect as stateConnect,
	disconnect as stateDisconnect,
	send as stateSend,
	updateAuth as stateUpdateAuth
} from './websocket.state.svelte';
import { unifiedDataUpdateOrchestrator } from '$lib/services/orchestration';

const log = createLogger('ws-actions');

/**
 * Pending subscription promises to avoid duplicates
 */
const pendingSubscriptions = new Map<string, Promise<void>>();

/**
 * Store EventBus listener cleanup functions for memory leak prevention
 */
const eventListeners: Array<{ unsubscribe: () => void }> = [];

/**
 * Message handler type definition
 */
type MessageHandler = (message: any) => void | Promise<void>;

/**
 * MessageRouter - Handler registry pattern for WebSocket messages
 *
 * Benefits:
 * - O(1) handler lookup vs O(n) switch statement
 * - Middleware support for logging, metrics, validation
 * - Type-safe handler registration
 * - Easy testing of individual handlers
 * - Extensible via middleware pattern
 *
 * Performance:
 * - Map.get() is O(1) vs linear switch scan
 * - Middleware execution is sequential
 * - Handler execution is async-safe
 */
class MessageRouter {
	private handlers = new Map<string, MessageHandler>();
	private middlewares: Array<(message: any, next: () => Promise<void>) => Promise<void>> = [];

	/**
	 * Register a single message handler
	 */
	register(type: string, handler: MessageHandler): void {
		if (this.handlers.has(type)) {
			log.warn(`⚠️ Overwriting handler for message type: ${type}`);
		}
		this.handlers.set(type, handler);
	}

	/**
	 * Register multiple handlers at once
	 */
	registerBatch(handlers: Record<string, MessageHandler>): void {
		Object.entries(handlers).forEach(([type, handler]) => {
			this.register(type, handler);
		});
	}

	/**
	 * Add middleware (logging, metrics, validation, etc.)
	 */
	use(middleware: (message: any, next: () => Promise<void>) => Promise<void>): void {
		this.middlewares.push(middleware);
	}

	/**
	 * Route message to registered handler
	 */
	async route(message: any): Promise<void> {
		const type = message.type;
		const handler = this.handlers.get(type);

		if (!handler) {
			log.warn(`⚠️ No handler registered for message type: ${type}`);
			return;
		}

		try {
			// Process immediately with middlewares
			let index = 0;
			const next = async (): Promise<void> => {
				if (index < this.middlewares.length) {
					const middleware = this.middlewares[index++];
					await middleware(message, next);
				} else {
					await handler(message);
				}
			};

			await next();
		} catch (error) {
			log.error(`❌ Error handling message type ${type}:`, { error });
			throw error;
		}
	}
}

/**
 * Global message router instance
 */
const router = new MessageRouter();

/**
 * MessageThrottler removed - Backend sends 1 message every 15 seconds.
 *
 * Throttling added 100ms delay with zero batching benefit for this use case.
 * Messages are already batched by the backend (complete state snapshots).
 *
 * If backend pattern changes to rapid incremental updates (>10 msg/sec),
 * restore MessageThrottler from git history.
 */

/**
 * Initialize message router with all handlers
 */
function initializeMessageRouter(): void {
	// Connection lifecycle handlers
	router.registerBatch({
		[MessageType.CONNECTION_ACK]: handleConnectionAck,
		[MessageType.CONNECTION_STATUS]: handleConnectionStatus,
		CONNECTION_ESTABLISHED: () => log.info('Connection established'),
		INITIAL_STATE: (message: any) => {
			log.info('Initial state received', message);
			// CRITICAL: Emit initial state for presence system
			if (message.users) {
				eventBus.emit('websocket:initial-state', message);
				log.info('📢 Initial state emitted to eventBus with user data');
			}
		},
		INITIAL_DATA_COMPLETE: () => log.info('Initial data complete')
	});

	// Subscription management handlers
	router.registerBatch({
		[MessageType.SUBSCRIPTION_ACK]: handleSubscriptionStatus,
		[MessageType.SUBSCRIPTION_STATUS]: handleSubscriptionStatus
	});

	// Data operations handlers
	router.registerBatch({
		[MessageType.DATA_UPDATE]: handleDataUpdate,
		[MessageType.DATA_INSERT]: handleDataInsert,
		[MessageType.DATA_DELETE]: handleDataDelete,
		[MessageType.BULK_UPDATE]: handleBulkUpdate
	});

	// =============================================================================
	// LEGACY HANDLERS REMOVED - Migration to DATA_DELTA/DATA_SNAPSHOT complete
	// =============================================================================
	// The following handlers have been removed as backend now sends unified
	// DATA_DELTA and DATA_SNAPSHOT messages:
	// - ALERTS_REFRESH_REQUIRED, BATCH_ALERTS_COMPLETE
	// - WORK_ORDERS_REFRESH_REQUIRED, TECH_WORK_ORDERS_UPDATE
	//
	// If you need to handle refresh signals, subscribe to:
	// - eventBus 'websocket:alerts:delta' for alert changes
	// - eventBus 'websocket:work_orders:delta' for work order changes
	// =============================================================================

	// Presence handlers
	router.register(MessageType.SYSTEM_PRESENCE_UPDATE, handleSystemPresenceUpdate);

	// Presence handlers (non-throttled)
	router.registerBatch({
		[MessageType.ONLINE_USERS]: handleOnlineUsers,
		[MessageType.ONLINE_USERS_RESPONSE]: handleOnlineUsers,
		[MessageType.USER_STATUS_RESPONSE]: handleUserStatusResponse,
		[MessageType.PRESENCE_UPDATE_SUCCESS]: handlePresenceUpdateSuccess,
		[MessageType.PRESENCE_STATS_RESPONSE]: handlePresenceStatsResponse,
		[MessageType.VIEWER_STATE_UPDATE]: handleViewerStateUpdate
	});

	// State management handlers
	router.register(MessageType.UPDATE, (message: any) => {
		// Backend sends UPDATE messages with nested state_update data
		if (message.data?.update_type === 'state_update') {
			const stateUpdate = {
				type: 'STATE_UPDATE',
				namespace: message.data.namespace,
				key: message.data.key,
				data: message.data.data,
				timestamp: message.data.timestamp || message.timestamp
			};
			log.info(`📥 UPDATE → STATE_UPDATE transformation:`, {
				namespace: stateUpdate.namespace,
				key: stateUpdate.key
			});
			handleStateUpdate(stateUpdate);
		} else {
			log.warn('Received UPDATE message without state_update type:', message);
		}
	});

	router.registerBatch({
		[MessageType.STATE_UPDATE]: handleStateUpdate,
		[MessageType.STATE_UPDATE_RESPONSE]: handleStateUpdateResponse,
		[MessageType.STATE_UPDATE_ACK]: () => log.debug('State update acknowledged'),
		[MessageType.STATE_DATA]: handleStateData
	});

	// Status and error handlers
	router.registerBatch({
		[MessageType.STATUS]: handleStatusUpdate,
		[MessageType.ERROR]: handleError,
		[MessageType.AUTH_ERROR]: handleError,
		[MessageType.CHAT_ERROR]: handleError,
		[MessageType.NOTIFICATION_ERROR]: handleError
	});

	// Heartbeat handlers (silent - transport handles these)
	router.registerBatch({
		[MessageType.HEARTBEAT]: () => {},
		[MessageType.PING]: () => {},
		[MessageType.PONG]: () => {}
	});

	// Notification handlers
	router.register(MessageType.NOTIFICATIONS_UPDATE, () =>
		log.debug('Notifications update received')
	);

	// Emergency broadcast handler
	router.register(MessageType.EMERGENCY_BROADCAST, handleEmergencyBroadcast);

	// Data confirmation handlers
	router.register(MessageType.DATA_SENT, () => log.debug('Data sent confirmation received'));

	// Data plane optimization handlers
	router.registerBatch({
		DATA_TICK: handleDataTick,
		DATA_HEARTBEAT: handleDataHeartbeat
	});

	// Unified DATA_DELTA / DATA_SNAPSHOT handlers (new unified pattern)
	router.registerBatch({
		[MessageType.DATA_DELTA]: handleDataDelta,
		[MessageType.DATA_SNAPSHOT]: handleDataSnapshot,
		[MessageType.TASK_PROGRESS]: handleTaskProgress
	});

	// Add logging middleware
	// NOTE: This should be sampled in production to avoid excessive logging overhead
	router.use(async (message: any, next: () => Promise<void>) => {
		log.debug(`📨 Received WebSocket message: type=${message.type}`);
		await next();
	});

	// Add performance monitoring middleware
	router.use(async (message: any, next: () => Promise<void>) => {
		const start = Date.now();
		await next();
		const duration = Date.now() - start;
		if (duration > 100) {
			log.warn(`⚠️ Slow handler for ${message.type}: ${duration}ms`);
		}
	});

	log.info('✅ Message router initialized with all handlers and middlewares');
}

/**
 * Main message handler
 * Routes incoming messages to registered handlers via MessageRouter
 */
function handleMessage(message: any): void {
	router.route(message).catch((error) => {
		log.error('❌ Critical error in message routing:', error);
	});
}

/**
 * Initialize message routing when in browser environment
 * Called once when actions layer loads
 */
if (browser) {
	// Initialize message router with all handlers and middlewares
	initializeMessageRouter();

	// Register the main message handler with state layer
	registerMessageHandler(handleMessage);
	log.info('Message routing initialized with handler registry pattern');

	// Listen for outgoing state updates (e.g., from ViewerService)
	eventListeners.push(
		eventBus.on('websocket:state:update', (payload: any) => {
			if (payload?.data) {
				// Forward to backend via STATE_UPDATE message
				sendMessage(MessageType.STATE_UPDATE, payload.data);
			}
		})
	);

	// CRITICAL: Listen for presence update requests from presence state
	eventListeners.push(
		eventBus.on('websocket:presence:update-status', (payload: any) => {
			const messageData = {
				status: payload.status,
				user_id: payload.user_id,
				display_name: payload.display_name,
				email: payload.email
			};

			log.info('📤 Sending presence update request with complete user data', payload);
			sendMessage(MessageType.UPDATE_USER_PRESENCE, messageData);
		})
	);

	eventListeners.push(
		eventBus.on('websocket:presence:request-online-users', (payload: any) => {
			log.info('📤 Requesting online users list', payload);
			sendMessage(MessageType.GET_ONLINE_USERS, payload || {});
		})
	);

	eventListeners.push(
		eventBus.on('websocket:presence:request-stats', (payload: any) => {
			log.info('📤 Requesting presence stats', payload);
			sendMessage(MessageType.GET_PRESENCE_STATS, payload || {});
		})
	);
}

/**
 * Message Handlers
 */

function handleConnectionAck(message: any): void {
	log.info('Connection acknowledged by server');
	eventBus.emit('websocket:connected', {});
}

function handleConnectionStatus(message: any): void {
	log.info('Connection status:', message.status);
}

function handleSubscriptionStatus(message: any): void {
	const { table, status, subscribed } = message;

	if (subscribed || status === 'subscribed') {
		websocketState.subscriptions.add(table);
		log.info(`Subscribed to table: ${table}`);
		// Emit event so subscribeToTable() promise can resolve
		eventBus.emit('websocket:subscribed', { table });
	} else {
		websocketState.subscriptions.delete(table);
		log.info(`Unsubscribed from table: ${table}`);
	}
}

function handleDataUpdate(message: any): void {
	const { table, data } = message;
	log.info(
		`📦 handleDataUpdate received: table=${table}, dataLength=${Array.isArray(data) ? data.length : 'not an array'}`
	);

	if (table && data) {
		// For alerts, store the full message structure (not just data array)
		if (table === 'alerts' && typeof data === 'object') {
			// Check if this is the new format with pre-filtered arrays
			if (
				(data as any).regular_alerts !== undefined ||
				(data as any).approved_alerts !== undefined
			) {
				log.info(
					`✅ Alerts data includes backend filtering: ${(data as any).regular_alerts?.length || 0} regular, ${(data as any).approved_alerts?.length || 0} approved`
				);
				websocketState.tables[table] = data; // Store full object
			} else if (Array.isArray(data)) {
				// Legacy format - just array
				websocketState.tables[table] = data;
			} else {
				log.warn(`⚠️ Unexpected alerts data format`);
				websocketState.tables[table] = data;
			}
		} else if (Array.isArray(data)) {
			// For other tables, store the array directly
			websocketState.tables[table] = data;
		} else {
			// Store as-is for non-array data
			websocketState.tables[table] = data;
		}

		websocketState.lastUpdated[table] = Date.now();
		log.info(`✅ Stored data in websocketState.tables['${table}']`);

		// Emit with the data (not the full structure for backward compatibility)
		const emitData = Array.isArray(data) ? data : (data as any).data || data;
		emitTableUpdate(table, emitData);
	} else {
		log.warn(`⚠️ Invalid data update: table=${table}, hasData=${!!data}`);
	}
}

function handleDataInsert(message: any): void {
	const { table, data } = message;
	if (table && data) {
		const currentData = websocketState.tables[table] || [];
		websocketState.tables[table] = [...currentData, data];
		websocketState.lastUpdated[table] = Date.now();
		emitTableUpdate(table, websocketState.tables[table]);
	}
}

function handleDataDelete(message: any): void {
	const { table, id } = message;
	if (table && id) {
		const currentData = websocketState.tables[table] || [];
		websocketState.tables[table] = currentData.filter((item: any) => item.id !== id);
		websocketState.lastUpdated[table] = Date.now();
		emitTableUpdate(table, websocketState.tables[table]);
	}
}

function handleBulkUpdate(message: any): void {
	const { table, data } = message;
	if (table && Array.isArray(data)) {
		websocketState.tables[table] = data;
		websocketState.lastUpdated[table] = Date.now();
		emitTableUpdate(table, data);
	}
}

// =============================================================================
// LEGACY HANDLERS REMOVED - Migration to DATA_DELTA/DATA_SNAPSHOT complete
// =============================================================================
// The following handlers have been removed as the backend now sends unified
// DATA_DELTA and DATA_SNAPSHOT messages instead:
// - handleAlertsUpdate (ALERTS_UPDATE) → Use handleDataSnapshot
// - handleAlertIncrementalUpdate (ALERT_INCREMENTAL_UPDATE) → Use handleDataDelta
// - handleAlertUpdate (ALERT_UPDATE) → Use handleDataDelta
// =============================================================================

// =============================================================================
// LEGACY HANDLERS REMOVED - Migration to DATA_DELTA/DATA_SNAPSHOT complete
// =============================================================================
// The following handlers have been removed:
// - handleAlertsRefreshRequired (ALERTS_REFRESH_REQUIRED)
// - handleBatchAlertsComplete (BATCH_ALERTS_COMPLETE)
// - handleWorkOrdersRefreshRequired (WORK_ORDERS_REFRESH_REQUIRED)
// - handleTechWorkOrdersUpdate (TECH_WORK_ORDERS_UPDATE)
// - handleAlertsRemovedForWorkOrder → handleDataDelta with entity='alerts', action='remove'
// - handleWorkOrdersUpdate (WORK_ORDERS_UPDATE) → handleDataSnapshot
// - handleWorkOrderIncrementalUpdate (WORK_ORDER_INCREMENTAL_UPDATE) → handleDataDelta
// =============================================================================

function handleSystemPresenceUpdate(message: any): void {
	// Emit to eventBus for presence state
	eventBus.emit('websocket:presence:system-update', message);
}

function handleOnlineUsers(message: any): void {
	// Emit to eventBus for presence state
	eventBus.emit('websocket:presence:online-users', message);
}

function handleUserStatusResponse(message: any): void {
	if (browser) {
		// Emit to eventBus for presence state
		eventBus.emit('websocket:presence:user-status', message);
	}
}

function handlePresenceUpdateSuccess(message: any): void {
	if (browser) {
		// Emit to eventBus for presence state
		eventBus.emit('websocket:presence:update-success', message);
	}
}

function handlePresenceStatsResponse(message: any): void {
	if (browser) {
		// Emit to eventBus for presence state
		eventBus.emit('websocket:presence:stats', message);
	}
}

function handleViewerStateUpdate(message: any): void {
	log.info('[VIEWER] 📥 Received VIEWER_STATE_UPDATE', {
		fullMessage: message
	});

	// Backend sends: { type: 'VIEWER_STATE_UPDATE', data: { itemId, userId, userName, action, ... } }
	const viewerData = message.data || message;
	const itemId = viewerData.itemId;
	const userId = viewerData.userId;
	const action = viewerData.action;

	log.info('[VIEWER] Extracted viewer data', {
		itemId,
		userId,
		userName: viewerData.userName,
		action
	});

	if (!itemId || !userId || !action) {
		log.warn('[VIEWER] ⚠️ Invalid viewer state update - missing required fields', {
			hasItemId: !!itemId,
			hasUserId: !!userId,
			hasAction: !!action
		});
		return;
	}

	const viewerPayload = {
		namespace: 'alerts_viewers',
		key: itemId,
		data: {
			[userId]:
				action === 'start_viewing'
					? {
							display_name: viewerData.userName,
							email: viewerData.userEmail,
							started_at: viewerData.timestamp
						}
					: null // null means user stopped viewing
		}
	};

	log.info('[VIEWER] ✅ Emitting to eventBus', viewerPayload);

	// Emit to ViewerService via eventBus
	if (browser) {
		eventBus.emit('websocket:viewer:update', viewerPayload);
		log.info('[VIEWER] 📢 Successfully emitted websocket:viewer:update to eventBus');
	}
}

function handleStateUpdate(message: any): void {
	const { namespace, key, data } = message;

	// Update viewer store if this is viewer data
	if (namespace && namespace.includes('viewer')) {
		viewerHelpers.updateViewers(namespace, key || '', data);
	}

	// CRITICAL: Handle user presence updates
	if (namespace === 'users' && key && data) {
		// Broadcast presence update sent from backend when a user's status changes
		eventBus.emit('websocket:presence-update', {
			user_id: key,
			status: data.status || 'online',
			display_name: data.display_name || data.name,
			last_seen: data.last_activity || new Date().toISOString(),
			metadata: data.metadata
		});
		log.info(`📢 User presence update emitted for user ${key}: ${data.status || 'online'}`);
	}

	eventBus.emit('websocket:state:update', { namespace, key, data });
}

function handleStateUpdateResponse(message: any): void {
	const { namespace, key } = message;
	eventBus.emit('websocket:state:update', { namespace, key, data: {} });
}

function handleStateData(message: any): void {
	const { namespace, key, data, timestamp } = message;

	// Update viewer store if this is viewer data
	if (namespace && namespace.includes('viewer')) {
		viewerHelpers.updateViewers(namespace, key || '', data);
	}

	eventBus.emit('websocket:state:data', { namespace, key, data, timestamp });
}

/**
 * Handle emergency broadcast messages
 * Emits to eventBus for EmergencyBroadcastModal to display
 */
function handleEmergencyBroadcast(message: any): void {
	log.info('🚨 Emergency broadcast received:', message);

	// Extract broadcast data from message
	const broadcastData = message.data || message;

	// Emit to eventBus for EmergencyBroadcastModal
	eventBus.emit('emergency:broadcast', {
		id: broadcastData.id,
		title: broadcastData.title,
		message: broadcastData.message,
		severity: broadcastData.severity || 'info',
		timestamp: broadcastData.timestamp || new Date().toISOString()
	});
}

function handleStatusUpdate(message: any): void {
	log.info('Status update:', message.status);
}

function handleError(message: any): void {
	const errorMsg = message.error || message.message || 'Unknown error';
	const errorCode = message.code || message.status || 'UNKNOWN';

	log.error(`WebSocket error (${message.type}):`, {
		message: errorMsg,
		code: errorCode,
		fullMessage: message
	});

	websocketState.error = errorMsg;

	// Handle specific authentication errors
	if (message.type === 'AUTH_ERROR') {
		if (errorCode === 'TOKEN_EXPIRED' || message.status === 'token_expired') {
			log.warn('[AUTH] Token expired - user needs to re-authenticate');
			// Emit event for auth service to handle
			eventBus.emit('websocket:auth:expired', {
				message: errorMsg,
				code: errorCode
			});

			// Redirect to login after a brief delay
			setTimeout(() => {
				if (browser) {
					window.location.href = '/login?reason=session_expired';
				}
			}, 2000);
		} else if (errorCode === 'TOKEN_INVALID' || message.status === 'token_invalid') {
			log.warn('[AUTH] Invalid token - user needs to re-authenticate');
			eventBus.emit('websocket:auth:invalid', {
				message: errorMsg,
				code: errorCode
			});

			// Redirect to login immediately
			setTimeout(() => {
				if (browser) {
					window.location.href = '/login?reason=invalid_token';
				}
			}, 1000);
		}
	}
}

function handleDataTick(message: any): void {
	// Data plane optimization - periodic batch updates
	log.debug('Data tick received');
}

function handleDataHeartbeat(message: any): void {
	// Data plane heartbeat
	log.debug('Data heartbeat received');
}

/**
 * Handle unified DATA_DELTA message (new unified pattern)
 *
 * This handler processes ALL incremental updates for all entity types.
 * It replaces the entity-specific handlers (AlertDeltaEvent, WorkOrderDeltaEvent, etc.)
 * and routes updates through the unified orchestrator.
 *
 * Entity types:
 * - 'alerts' → routes to 'alerts' orchestrator
 * - 'work_orders' → routes to 'workOrders' orchestrator
 * - 'approved_alerts' → routes to 'alerts' orchestrator (same data, different filter)
 * - 'notifications' → routes to 'notifications' orchestrator
 */
async function handleDataDelta(message: DataDeltaMessage): Promise<void> {
	const { entity, action, id, ids, data, reason, change_ts, metadata } = message;

	// 🔍 TIMING: Track each stage of DATA_DELTA processing
	const timings: Record<string, number> = {};
	const startTotal = performance.now();

	log.info('[DATA_DELTA] ⏱️ [1/5] Received unified delta update', {
		entity,
		action,
		id,
		idsCount: ids?.length,
		reason,
		hasData: !!data
	});

	// Log work_orders updates with full data for debugging
	if (entity === 'work_orders') {
		log.info('[DATA_DELTA] 🔍 Work order update data:', {
			id,
			assigned_to: data?.assigned_to,
			status: data?.status,
			wo_number: data?.wo_number,
			fullDataKeys: data ? Object.keys(data) : []
		});
	}

	try {
		if (!browser) return;

		timings.setup = performance.now() - startTotal;

		// Map entity to orchestrator dataType
		// Note: 'approved_alerts' uses the same 'alerts' orchestrator
		const dataTypeMap: Record<string, 'alerts' | 'workOrders' | 'approvedAlerts' | 'notifications'> = {
			alerts: 'alerts',
			work_orders: 'workOrders',
			approved_alerts: 'alerts', // Same state, different view
			notifications: 'notifications'
		};

		const dataType = dataTypeMap[entity];
		if (!dataType) {
			log.warn(`[DATA_DELTA] Unknown entity type: ${entity}`);
			return;
		}

		// Action already matches orchestrator operations (add/update/remove)
		// No mapping needed - backend uses same terminology
		const operation = action;

		// Prepare update data
		// For remove with ids array, pass the ids directly
		// For single item operations, wrap data in array
		let updateData: any;
		if (action === 'remove' && ids && ids.length > 0) {
			// Bulk removal - pass array of IDs
			updateData = ids;
		} else if (data && Object.keys(data).length > 0) {
			// Single item update - wrap in array
			updateData = [data];
		} else {
			log.warn('[DATA_DELTA] No data provided for non-remove operation');
			return;
		}

		timings.dataPrep = performance.now() - startTotal - timings.setup;
		log.info('[DATA_DELTA] ⏱️ [2/5] Data prepared, calling orchestrator', { timings });

		// Route through orchestrator
		const orchestratorStart = performance.now();
		await unifiedDataUpdateOrchestrator.processUpdate(
			dataType,
			operation,
			updateData,
			{
				source: 'websocket',
				metadata: {
					messageType: 'DATA_DELTA',
					entity,
					action,
					id,
					ids,
					reason,
					change_ts,
					timestamp: message.timestamp || Date.now(),
					...metadata
				}
			}
		);
		timings.orchestrator = performance.now() - orchestratorStart;

		log.info(`[DATA_DELTA] ⏱️ [3/5] ✅ Orchestrator completed`, {
			entity,
			action,
			orchestratorMs: timings.orchestrator.toFixed(0)
		});

		// Emit to eventBus for backward compatibility
		// Services may listen for specific entity events
		const eventBusStart = performance.now();
		eventBus.emit(`websocket:${entity}:delta`, {
			action,
			id,
			ids,
			data,
			reason,
			change_ts
		});
		timings.eventBus = performance.now() - eventBusStart;

		timings.total = performance.now() - startTotal;
		log.info(`[DATA_DELTA] ⏱️ [5/5] 📊 TIMING SUMMARY`, {
			entity,
			action,
			setup: `${timings.setup.toFixed(0)}ms`,
			dataPrep: `${timings.dataPrep.toFixed(0)}ms`,
			orchestrator: `${timings.orchestrator.toFixed(0)}ms`,
			eventBus: `${timings.eventBus.toFixed(0)}ms`,
			total: `${timings.total.toFixed(0)}ms`,
			bottleneck: Object.entries(timings).reduce((a, b) => b[1] > a[1] ? b : a)[0]
		});

	} catch (error) {
		log.error('[DATA_DELTA] Failed to process delta update:', error as Error);
		eventBus.emit('websocket:error', {
			type: 'data-delta-failed',
			error: error as Error,
			context: { entity, action, id }
		});
	}
}

/**
 * Handle unified DATA_SNAPSHOT message (new unified pattern)
 *
 * This handler processes ALL full data loads for all entity types.
 * It replaces the entity-specific snapshot handlers (AlertsUpdateMessage, WorkOrdersUpdateMessage, etc.)
 * and routes updates through the unified orchestrator with 'replace' operation.
 *
 * Entity types:
 * - 'alerts' → routes to 'alerts' orchestrator
 * - 'work_orders' → routes to 'workOrders' orchestrator
 * - 'approved_alerts' → routes to 'alerts' orchestrator
 * - 'notifications' → routes to 'notifications' orchestrator
 */
async function handleDataSnapshot(message: DataSnapshotMessage): Promise<void> {
	const { entity, data, count, is_initial, is_partial, groups, metadata } = message;

	try {
		if (!browser) return;

		// Map entity to orchestrator dataType
		const dataTypeMap: Record<string, 'alerts' | 'workOrders' | 'approvedAlerts' | 'notifications'> = {
			alerts: 'alerts',
			work_orders: 'workOrders',
			approved_alerts: 'alerts',
			notifications: 'notifications'
		};

		const dataType = dataTypeMap[entity];
		if (!dataType) {
			log.warn(`[DATA_SNAPSHOT] Unknown entity type: ${entity}`);
			return;
		}

		// Use 'replace' only for initial loads (page refresh, first connection)
		// Use 'merge' for non-initial snapshots to preserve incremental updates from DATA_DELTA
		// This prevents stale cached data from overwriting correct incremental state
		// Example: DATA_DELTA adds work order (131→132), then stale DATA_SNAPSHOT would reset (132→131)
		const operation = is_initial ? 'replace' : (is_partial ? 'merge' : 'merge');

		// Store table key for backward compatibility
		const tableKey = entity === 'work_orders' ? 'work_orders' : entity;

		// Handle alerts data FIRST - backend now sends pre-partitioned object format
		if (entity === 'alerts') {
			// Check for new pre-partitioned format: { regular_alerts: [...], approved_alerts: [...] }
			const alertData = data as any;
			if (alertData?.regular_alerts && alertData?.approved_alerts) {
				// New format - backend already partitioned, route directly to orchestrator
				log.info('[DATA_SNAPSHOT] Using pre-partitioned alerts format', {
					regularGroups: alertData.regular_alerts.length,
					approvedGroups: alertData.approved_alerts.length
				});

				await unifiedDataUpdateOrchestrator.processUpdate(
					dataType,
					operation,
					{
						groupedRegularAlerts: alertData.regular_alerts,
						groupedApprovedAlerts: alertData.approved_alerts
					},
					{
						source: 'websocket',
						metadata: {
							messageType: 'DATA_SNAPSHOT',
							entity,
							count,
							isInitial: is_initial,
							isPartial: is_partial,
							groups,
							timestamp: message.timestamp || Date.now(),
							...metadata
						}
					}
				);
				return;
			}

			// If we reach here, alerts data is in unexpected format
			log.error('[DATA_SNAPSHOT] Unexpected alerts format - expected { regular_alerts, approved_alerts }', {
				dataType: typeof data,
				isArray: Array.isArray(data)
			});
			return;
		}

		// For non-alerts entities, data must be an array
		if (!Array.isArray(data)) {
			log.warn('[DATA_SNAPSHOT] Data is not an array', { entity });
			return;
		}

		// Store in websocketState for backward compatibility
		websocketState.tables[tableKey] = data as unknown as TableItem[];
		websocketState.lastUpdated[tableKey] = Date.now();

		// Route through orchestrator for non-alerts entities
		await unifiedDataUpdateOrchestrator.processUpdate(
			dataType,
			operation,
			data,
			{
				source: 'websocket',
				metadata: {
					messageType: 'DATA_SNAPSHOT',
					entity,
					count,
					isInitial: is_initial,
					isPartial: is_partial,
					groups,
					timestamp: message.timestamp || Date.now(),
					...metadata
				}
			}
		);

		// Emit to eventBus for approved_alerts - AlertsService backward compatibility
		if (entity === 'approved_alerts') {
			emitTableUpdate(tableKey, data);
		}

	} catch (error) {
		log.error('[DATA_SNAPSHOT] Failed to process snapshot:', error as Error);
		eventBus.emit('websocket:error', {
			type: 'data-snapshot-failed',
			error: error as Error,
			context: { entity, count }
		});
	}
}

function handleTaskProgress(message: TaskProgressMessage): void {
	eventBus.emit('websocket:task:progress', {
		task_id: message.task_id,
		task_type: message.task_type,
		stage: message.stage,
		status: message.status,
		progress: message.progress,
		message: message.message,
		metadata: message.metadata,
		timestamp: message.timestamp
	});
}

/**
 * Helper: Emit table update event to BOTH window AND eventBus
 * CRITICAL: Services listen on eventBus, so we must emit to both systems
 */
function emitTableUpdate(table: string, data: any): void {
	if (browser) {
		// Emit to EventBus for AlertsService/WorkOrderService
		eventBus.emit('websocket:table:update', { table, data });
	}
}

/**
 * Public API - Connection Management
 */

/**
 * Connect to WebSocket server
 */
export async function connect(): Promise<void> {
	if (!browser) {
		log.warn('🔌 Cannot connect: not in browser environment');
		return Promise.resolve();
	}

	try {
		console.log('═══════════════════════════════════════════════════════════');
		log.info('🔌 [1/3] Connection attempt started', {
			url: websocketState.url || 'unknown'
		});

		await stateConnect();

		log.info('🔌 [3/3] WebSocket connected successfully', {
			readyState: websocketState.connected ? 'OPEN' : 'unknown'
		});
		console.log('═══════════════════════════════════════════════════════════');
	} catch (error) {
		log.error('🔌 Connection failed', {
			error: error instanceof Error ? error.message : String(error)
		});
		console.log('═══════════════════════════════════════════════════════════');
		throw error;
	}
}

/**
 * Disconnect from WebSocket server
 */
export function disconnect(): void {
	if (!browser) {
		return;
	}

	stateDisconnect(true);

	// Clear pending subscriptions
	pendingSubscriptions.clear();

	// Clear tables and subscriptions
	websocketState.tables = {};
	websocketState.lastUpdated = {};
	websocketState.subscriptions = new SvelteSet();

	log.info('WebSocket disconnected');
}

/**
 * Cleanup function to prevent memory leaks
 * Called on module cleanup or hot reload
 */
export function cleanup(): void {
	// Remove all EventBus listeners to prevent memory leaks
	log.info('[WebSocket Actions] Cleaning up EventBus listeners:', { count: eventListeners.length });
	eventListeners.forEach((subscription) => subscription.unsubscribe());
	eventListeners.length = 0;

	// EXISTING cleanup code
	stateDisconnect(true);
	pendingSubscriptions.clear();
	websocketState.tables = {};
	websocketState.lastUpdated = {};
	websocketState.subscriptions = new SvelteSet();

	log.info('[WebSocket Actions] Cleanup complete');
}

/**
 * Public API - Table Subscriptions
 */

/**
 * Subscribe to a table
 *
 * RACE CONDITION FIX: This function now uses atomic promise registration
 * to prevent duplicate subscriptions when called concurrently.
 *
 * Race Condition Analysis:
 * - PROBLEM: Two concurrent calls can create duplicate subscriptions
 * - ROOT CAUSE: Promise registration happened AFTER promise creation
 * - WINDOW: Lines 1014-1047 allowed concurrent calls to both pass the has() check
 * - SOLUTION: Move registration to IMMEDIATELY after variable creation (atomic operation)
 *
 * Example Race Scenario (BEFORE FIX):
 * Thread A                          Thread B
 * -------                          -------
 * has(table)? NO ✓
 *                                  has(table)? NO ✓ (passes before A registers!)
 * create promise (line 1014)
 *                                  create promise (line 1014)
 * ... promise constructor ...
 *                                  ... promise constructor ...
 * set(table, promise) (line 1047)
 *                                  set(table, promise) (line 1047) - DUPLICATE!
 *
 * After Fix: Registration happens immediately, making check-and-set atomic
 */
export async function subscribeToTable(
	tableName: string,
	filters?: Record<string, unknown>
): Promise<void> {
	if (!browser) {
		return Promise.resolve();
	}

	// CRITICAL SECTION START - Check and register must be atomic
	// If already subscribed, don't resubscribe
	if (websocketState.subscriptions.has(tableName)) {
		log.info(`Already subscribed to ${tableName}`);
		return Promise.resolve();
	}

	// If there's already a pending subscription, return that promise
	// This check MUST happen before creating new promise to prevent race
	// ATOMIC GUARD: This is the first line of defense against duplicates
	if (pendingSubscriptions.has(tableName)) {
		log.info(`Subscription to ${tableName} already pending`);
		return pendingSubscriptions.get(tableName)!;
	}

	// Create subscription promise
	// RACE CONDITION FIX: We create the promise and register it IMMEDIATELY
	// to prevent concurrent calls from creating duplicate subscriptions.
	//
	// The key insight: By setting the promise in the Map BEFORE the Promise
	// constructor finishes executing, we make the check-and-set operation
	// effectively atomic. Any concurrent call will see the Map entry and
	// return early before attempting to create its own promise.
	let subscriptionPromise: Promise<void>;
	subscriptionPromise = new Promise<void>((resolve, reject) => {
		// ATOMIC REGISTRATION: This promise is registered in the Map immediately
		// after variable creation (line below), closing the race window
		pendingSubscriptions.set(tableName, subscriptionPromise);

		const timeout = setTimeout(() => {
			pendingSubscriptions.delete(tableName);
			reject(new Error(`Subscription to ${tableName} timed out`));
		}, 10000);

		// Listen for subscription acknowledgment via EventBus
		const subscription = eventBus.on('websocket:subscribed', (data: any) => {
			if (data?.table === tableName) {
				clearTimeout(timeout);
				pendingSubscriptions.delete(tableName);
				subscription.unsubscribe();
				resolve();
			}
		});

		// Send subscription message
		const success = stateSend({
			type: MessageType.SUBSCRIBE,
			table: tableName,
			filters: filters || {}
		});

		if (!success) {
			clearTimeout(timeout);
			pendingSubscriptions.delete(tableName);
			subscription.unsubscribe();
			reject(new Error('Failed to send subscription message'));
		}
	});

	// CRITICAL SECTION END

	return subscriptionPromise;
}

/**
 * Unsubscribe from a table
 */
export function unsubscribeFromTable(tableName: string): void {
	if (!browser) {
		return;
	}

	// Remove any pending subscription
	pendingSubscriptions.delete(tableName);

	// Send unsubscribe message
	stateSend({
		type: MessageType.UNSUBSCRIBE,
		table: tableName
	});

	// Update state
	websocketState.subscriptions.delete(tableName);

	log.info(`Unsubscribed from ${tableName}`);
}

/**
 * Public API - Messaging
 */

/**
 * Send a message to the WebSocket server
 *
 * @param type - Message type
 * @param payload - Message payload (optional)
 * @param priority - Message priority for queue management (optional, default: 'normal')
 */
export function sendMessage(
	type: string,
	payload?: object,
	priority?: 'critical' | 'high' | 'normal' | 'low'
): void {
	if (!browser) {
		return;
	}

	// Wrap payload in 'data' field to avoid type collision
	// The backend expects: { type: 'MESSAGE_TYPE', data: {...payload} }
	const message = payload
		? {
				type,
				data: payload,
				timestamp: Date.now()
			}
		: {
				type,
				timestamp: Date.now()
			};

	const success = stateSend(message, priority);
	if (!success) {
		log.warn(`Failed to send message: ${type} (queued for retry)`);
	}
}

/**
 * Request data from server
 * @param dataType - Type of data to request (alerts, work_orders, all)
 * @param reason - Reason for the request (manual_refresh, initial_data_missing, etc.)
 * @param alertType - Optional alert type filter (regular, approved) - only used when dataType is 'alerts'
 */
export function requestData(
	dataType: string,
	reason: string = 'manual_request',
	alertType?: 'regular' | 'approved'
): void {
	const payload: Record<string, string> = {
		dataType,
		reason
	};

	// Only include alertType for alerts requests
	if (dataType === 'alerts' && alertType) {
		payload.alertType = alertType;
	}

	sendMessage('REQUEST_DATA', payload);
	log.info(`Requested data: ${dataType}${alertType ? ` (alertType: ${alertType})` : ''} (reason: ${reason})`);
}

/**
 * Public API - Presence Operations
 */

/**
 * Get online users
 */
export function getOnlineUsers(includeDetails?: boolean): void {
	sendMessage(MessageType.GET_ONLINE_USERS, { includeDetails });
}

/**
 * Get user status
 */
export function getUserStatus(userId: string): void {
	sendMessage(MessageType.GET_USER_STATUS, { userId });
}

/**
 * Update user presence
 */
export function updateUserPresence(status: 'online' | 'away' | 'busy' | 'offline'): void {
	sendMessage(MessageType.UPDATE_USER_PRESENCE, { status });
}

/**
 * Get presence stats
 */
export function getPresenceStats(): void {
	sendMessage(MessageType.GET_PRESENCE_STATS);
}

/**
 * Public API - Utility Functions
 */

/**
 * Check if connected
 */
export function isConnected(): boolean {
	return websocketState.connected;
}

/**
 * Check if subscribed to table
 */
export function isSubscribedToTable(tableName: string): boolean {
	return websocketState.subscriptions.has(tableName);
}

/**
 * Update authentication credentials
 */
export function updateAuth(authToken: string, authType: string = 'Bearer'): void {
	log.info('🔌 [2/3] Updating WebSocket auth credentials', {
		hasToken: !!authToken,
		authType,
		connected: websocketState.connected
	});

	stateUpdateAuth(authToken, authType);

	log.info('🔌 Auth credentials updated', {
		willReconnect: websocketState.connected
	});
}
